/* eslint-disable indent */
// import './style.scss';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  downloadPdfFile as downloadPdfPrequalification,
  downloadDocxFile as downloadDocxPrequalification,
} from 'apis/prequalificationReviews.api';
import {
  downloadPdfFile as downloadPdfSpecializeReview,
  downloadDocxFile as downloadDocxSpecializeReview,
} from 'apis/specializeReviews.api';
import {
  downloadPdfFile as downloadPdfFinancialReview,
  downloadDocxFile as downloadDocxFinancialReview,
} from 'apis/financialReviews.api';
import {
  downloadPdfFile as downloadPdfMidtermReview,
  downloadDocxFile as downloadDocxMidtermReview,
} from 'apis/midtermReviews.api';
import {
  downloadPdfFile as downloadPdfAcceptanceReview,
  downloadDocxFile as downloadDocxAcceptanceReview,
} from 'apis/acceptanceReviews.api';
import { getAllVersions } from 'apis/projectProposals.api';
import { toast } from 'layout';
import { TOAST, COUNCIL_TYPE_CODE } from 'constant';
import {
  isMemberAllowedReview,
  getAcadamicLevelShortHand,
  toNonAccentVietnamese,
  is1593,
  getOrganizationNameById,
} from 'utils/func';
import {
  downloadDocxFile as downloadDocxDynamicReview,
  downloadPdfFile as downloadPdfDynamicReview,
} from 'apis/reviews.api';

dayjs.extend(utc);
dayjs.extend(timezone);
export default function Members({
  members,
  council,
  councilType,
  organizations,
  /* eslint-disable no-unused-vars */
  setIsFormModalOpen,
  setIsDetailModalOpen,
  setCouncilMemberId,
  projectProposalId,
  councilId,
  isShowScore,
  isShowProposedExpenditure,
  isShowResult,
  isShowValidScore,
  yearProposed,
  template,
  isShowStatus,
  isShowResultReviewPrequalification,
}) {
  // #region Data
  const { t } = useTranslation();
  const { projectId, summaryReportId } = useParams();
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  const [isVisibleConfirmSendRemind, setIsVisibleConfirmSendRemind] = useState(false);

  // #endregion Data

  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId),
    {
      enabled: !!projectProposalId,
    }
  );

  const version1 = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 1) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);
  const prequalificationVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 2) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);

  const specializationVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return allProjectProposalVersionsData?.data?.find((item) => item?.version === 3) ?? null;
    }
    return null;
  }, [allProjectProposalVersionsData]);

  const latestVersion = useMemo(
    () => allProjectProposalVersionsData?.data?.reduce((a, b) => (a?.version > b?.version ? a : b)),
    [allProjectProposalVersionsData]
  );

  // #region Event
  const {
    mutate: mutateDownloadPdfPrequalificationReview,
    isLoading: isDownloadPdfPrequalificationReviewLoading,
  } = useMutation(downloadPdfPrequalification);
  const {
    mutateAsync: mutateAsyncDownloadPdfDynamicReview,
    isLoading: isDownloadPdfDynamicReviewLoading,
  } = useMutation(downloadPdfDynamicReview);
  const {
    mutate: mutateDownloadDocxPrequalificationReview,
    isLoading: isDownloadDocxPrequalificationReviewLoading,
  } = useMutation(downloadDocxPrequalification);
  const {
    mutateAsync: mutateAsyncDownloadDocxDynamicReview,
    isLoading: isDownloadDocxDynamicReviewLoading,
  } = useMutation(downloadDocxDynamicReview);
  const {
    mutate: mutateDownloadPdfSpecializeReview,
    isLoading: isDownloadPdfSpecializeReviewLoading,
  } = useMutation(downloadPdfSpecializeReview);
  const {
    mutate: mutateDownloadDocxSpecializeReview,
    isLoading: isDownloadDocxSpecializeReviewLoading,
  } = useMutation(downloadDocxSpecializeReview);

  const {
    mutate: mutateDownloadPdfFinancialReview,
    isLoading: isDownloadPdfFinancialReviewLoading,
  } = useMutation(downloadPdfFinancialReview);
  const {
    mutate: mutateDownloadDocxFinancialReview,
    isLoading: isDownloadDocxFinancialReviewLoading,
  } = useMutation(downloadDocxFinancialReview);

  const { mutate: mutateDownloadPdfMidtermReview, isLoading: isDownloadPdfMidtermReviewLoading } =
    useMutation(downloadPdfMidtermReview);
  const { mutate: mutateDownloadDocxMidtermReview, isLoading: isDownloadDocxMidtermReviewLoading } =
    useMutation(downloadDocxMidtermReview);

  const {
    mutate: mutateDownloadPdfAcceptanceReview,
    isLoading: isDownloadPdfAcceptanceReviewLoading,
  } = useMutation(downloadPdfAcceptanceReview);
  const {
    mutate: mutateDownloadDocxAcceptanceReview,
    isLoading: isDownloadDocxAcceptanceReviewLoading,
  } = useMutation(downloadDocxAcceptanceReview);

  const handleDownloadReview = async (res, value, type, fileType = 'pdf') => {
    if (res?.data) {
      const file = new Blob([res.data]);

      const academicLevelShorthand = getAcadamicLevelShortHand(
        value?.contact?.scientificProfile?.academicRank,
        value?.contact?.scientificProfile?.academicDegree,
        t,
        true
      );
      const filename = `Phieu nhan xet danh gia ${type} - ${academicLevelShorthand}${toNonAccentVietnamese(
        value?.contact?.fullname
      )}.${fileType}`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = filename;
      link.click();
    }
  };

  const handleDownloadPdf = async (value) => {
    setSelectedMemberId(value?.id);

    if (councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      const pdfData = {
        projectProposalId,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfPrequalificationReview(pdfData, {
          onSuccess: (res) => {
            handleDownloadReview(res, value, 'so tuyen');
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'so tuyen');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      const pdfData = {
        projectProposalId: prequalificationVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfSpecializeReview(pdfData, {
          onSuccess: (res) => {
            handleDownloadReview(res, value, 'chuyen mon');
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'chuyen mon');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      const pdfData = {
        projectProposalId: specializationVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      mutateDownloadPdfFinancialReview(pdfData, {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const academicLevelShorthand = getAcadamicLevelShortHand(
              value?.contact?.scientificProfile?.academicRank,
              value?.contact?.scientificProfile?.academicDegree,
              t,
              true
            );
            const filename = `Phieu nhan xet danh gia tham dinh kinh phi - ${academicLevelShorthand}${toNonAccentVietnamese(
              value?.contact?.fullname
            )}.pdf`;

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;
            link.click();
          }
        },
        onError: (err) => {
          err.response.data.text().then((promiseResult) => {
            toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
          });
        },
      });
    } else if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      const pdfData = {
        projectProposalId: latestVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      mutateDownloadPdfMidtermReview(pdfData, {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const academicLevelShorthand = getAcadamicLevelShortHand(
              value?.contact?.scientificProfile?.academicRank,
              value?.contact?.scientificProfile?.academicDegree,
              t,
              true
            );
            const filename = `Phieu nhan xet danh gia giua ki - ${academicLevelShorthand}${toNonAccentVietnamese(
              value?.contact?.fullname
            )}.pdf`;

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;
            link.click();
          }
        },
        onError: (err) => {
          err.response.data.text().then((promiseResult) => {
            toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
          });
        },
      });
    } else if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      const pdfData = {
        projectProposalId: latestVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        mutateDownloadPdfAcceptanceReview(pdfData, {
          onSuccess: (res) => {
            if (res?.data) {
              handleDownloadReview(res, value, 'nghiem thu');
            }
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        });
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'nghiem thu');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      const pdfData = {
        projectProposalId: latestVersion?.id,
        councilId,
        councilMemberId: value?.id,
      };
      if (!is1593(template)) {
        const res = await mutateAsyncDownloadPdfDynamicReview(pdfData);
        handleDownloadReview(res, value, 'thanh ly');
      }
    }
  };

  const handleDownloadDocx = async (value) => {
    setSelectedMemberId(value?.id);

    if (councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxPrequalificationReview(
          {
            projectProposalId,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              handleDownloadReview(res, value, 'so tuyen', 'docx');
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'so tuyen', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxSpecializeReview(
          {
            projectProposalId: prequalificationVersion?.id,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              handleDownloadReview(res, value, 'chuyen mon', 'docx');
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'chuyen mon', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      mutateDownloadDocxFinancialReview(
        {
          projectProposalId: specializationVersion?.id,
          councilId,
          councilMemberId: value?.id,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);

              const academicLevelShorthand = getAcadamicLevelShortHand(
                value?.contact?.scientificProfile?.academicRank,
                value?.contact?.scientificProfile?.academicDegree,
                t,
                true
              );
              const filename = `Phieu nhan xet danh gia tham dinh kinh phi - ${academicLevelShorthand}${toNonAccentVietnamese(
                value?.contact?.fullname
              )}.docx`;

              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        }
      );
    } else if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      mutateDownloadDocxMidtermReview(
        {
          projectProposalId: latestVersion?.id,
          councilId,
          councilMemberId: value?.id,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);

              const academicLevelShorthand = getAcadamicLevelShortHand(
                value?.contact?.scientificProfile?.academicRank,
                value?.contact?.scientificProfile?.academicDegree,
                t,
                true
              );
              const filename = `Phieu nhan xet danh gia giua ki - ${academicLevelShorthand}${toNonAccentVietnamese(
                value?.contact?.fullname
              )}.docx`;

              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: (err) => {
            err.response.data.text().then((promiseResult) => {
              toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
            });
          },
        }
      );
    } else if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      if (!is1593(template)) {
        mutateDownloadDocxAcceptanceReview(
          {
            projectProposalId: latestVersion?.id,
            councilId,
            councilMemberId: value?.id,
          },
          {
            onSuccess: (res) => {
              if (res?.data) {
                handleDownloadReview(res, value, 'nghiem thu', 'docx');
              }
            },
            onError: (err) => {
              err.response.data.text().then((promiseResult) => {
                toast(TOAST.ERROR, t(`errorMessage.${JSON.parse(promiseResult)?.code}`));
              });
            },
          }
        );
      } else if (is1593(template)) {
        const res = await mutateAsyncDownloadDocxDynamicReview({
          projectProposalId,
          councilId,
          councilMemberId: value?.id,
        });
        handleDownloadReview(res, value, 'nghiem thu', 'docx');
      }
    } else if (councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      const res = await mutateAsyncDownloadDocxDynamicReview({
        projectProposalId,
        councilId,
        councilMemberId: value?.id,
      });
      handleDownloadReview(res, value, 'thanh ly', 'docx');
    }
  };

  const sumScoreInputFormOfMember = (score) => {
    const sum =
      (score?.scoreImportance ?? 0) +
      (score?.scoreQuality ?? 0) +
      (score?.scoreProduct ?? 0) +
      (score?.scoreCapability ?? 0) +
      (score?.scoreSubCriteria ?? 0);

    return sum;
  };

  // #endregion Event

  const formatIndex = (value, { rowIndex }) => rowIndex + 1;

  const formatFullname = (value) =>
    `${getAcadamicLevelShortHand(
      value?.contact?.scientificProfile?.academicRank,
      value?.contact?.scientificProfile?.academicDegree,
      t,
      true
    )}${value?.contact?.fullname}`;

  const formatOrganization = (value) =>
    getOrganizationNameById(
      value?.contact?.organizationId,
      organizations,
      value?.contact?.specificOrganizationName,
      t
    );

  const formatRole = (value) => (
    <div className="flex">
      <div className="w-2 mr-2">
        {isShowValidScore &&
          isMemberAllowedReview(value) &&
          (value?.projectProposalCouncilMembers?.length === 0 ||
            value?.projectProposalCouncilMembers?.[0]?.isCalculateScore) && (
            <>
              <i
                className="tick-icon pi pi-check border-primary-700"
                data-pr-tooltip={t('council.member.fields.tooltipAllowedCalScore')}
                data-pr-position="left"
              />
              <Tooltip target=".tick-icon" />
            </>
          )}
      </div>
      {value?.councilMemberRole?.code && t(`council.role.${value?.councilMemberRole?.code}`)}
    </div>
  );

  const formatResult = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    if (resultStatus === 'notReviewed' && !isSubmitted) {
      return '';
    }

    let result = null;
    if (!is1593(template)) {
      if (councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
        const evaluateGeneral =
          value?.projectProposalCouncilMembers[0]?.midtermReview?.evaluateGeneral;
        if (evaluateGeneral != null) {
          result = evaluateGeneral === false ? 'Chưa đạt' : 'Đạt';
        } else {
          result = null;
        }
      }

      if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
        const scoreTarget = value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreTarget;
        const scoreContent =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreContent;
        const scoreApproach =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreApproach;
        const scoreProcess =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreProcess;
        const scoreApplicationProduct =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreApplicationProduct;
        const scorePublishedProduct =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scorePublishedProduct;
        const scoreTrainingProduct =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreTrainingProduct;
        const scoreReportQuality =
          value?.projectProposalCouncilMembers[0]?.acceptanceReview?.scoreReportQuality;

        const scoreTotal =
          scoreTarget +
          scoreContent +
          scoreApproach +
          scoreProcess +
          scoreApplicationProduct +
          scorePublishedProduct +
          scoreTrainingProduct +
          scoreReportQuality;

        result = scoreTotal || null;
      }
    } else if (is1593(template)) {
      if (councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
        const rank = value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.rank;
        if (rank && typeof rank === 'object') {
          Object.keys(rank).forEach((key) => {
            if (rank[key] === true) {
              result = t(`council.acceptanceReview.rank.${key}`);
            }
          });
        }
      }
    }

    return result;
  };

  const formatScore = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;

    if (isMemberAllowedReview(value)) {
      const sumScoreStaticForm = sumScoreInputFormOfMember(
        value?.projectProposalCouncilMembers?.[0]?.specializeReview
      );
      if (sumScoreStaticForm > 0) {
        return sumScoreStaticForm;
      }

      const sumScoreDynamicForm =
        value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.evaluation?.totalScore;
      if (sumScoreDynamicForm > 0) {
        return sumScoreDynamicForm;
      }
    }

    return '';
  };

  const formatResultReviewPrequalification = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    // Check if the council is not reviewed and the member is not allowed to review
    if ((resultStatus === 'notReviewed' && !isSubmitted) || !isMemberAllowedReview(value)) {
      return null;
    }

    let resultReview;

    // Check with template review is not new (1593)
    if (yearProposed < 2025) {
      resultReview =
        value?.projectProposalCouncilMembers?.[0]?.prequalificationReview?.generalEvaluate;
    } else {
      const conclusion =
        value?.projectProposalCouncilMembers?.[0]?.review?.reviewValues?.conclusion;
      if (conclusion?.qualified === true) {
        resultReview = true;
      } else if (conclusion?.notQualified === true) {
        resultReview = false;
      }
    }

    // If resultReview is null or undefined, return null
    if (resultReview === null || resultReview === undefined) {
      return null;
    }

    return (
      <Tag
        value={resultReview === true ? t('resultReview.qualified') : t('resultReview.notQualified')}
        severity={resultReview === true ? 'success' : 'danger'}
        style={{ minWidth: '6rem', textAlign: 'center' }}
        rounded
      />
    );
  };

  const formatStatus = (value) => {
    if (!isMemberAllowedReview(value)) {
      return null;
    }

    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    return (
      <Tag
        value={isSubmitted ? t('statusReview.submitted') : t('statusReview.notSubmitted')}
        severity={isSubmitted ? 'success' : 'info'}
        style={{ minWidth: '6rem', textAlign: 'center' }}
        rounded
      />
    );
  };

  const formatProposedExpenditure = (value) => {
    const isSubmitted = value?.projectProposalCouncilMembers?.[0]?.isSubmitted;
    const resultStatus = council?.projectProposalCouncils?.[0]?.councilStatus;

    if (resultStatus === 'notReviewed' && !isSubmitted) {
      return '';
    }

    const totalSuggestionExpenditureFromVNUHCM =
      value?.projectProposalCouncilMembers?.[0]?.financialReview
        ?.totalSuggestionExpenditureFromVNUHCM;
    const totalSuggestionExpenditureFromMobilizedCapital =
      value?.projectProposalCouncilMembers?.[0]?.financialReview
        ?.totalSuggestionExpenditureFromMobilizedCapital;

    if (!value?.projectProposalCouncilMembers?.[0]?.financialReview) {
      return '';
    }
    return (
      (totalSuggestionExpenditureFromVNUHCM ?? 0) +
      (totalSuggestionExpenditureFromMobilizedCapital ?? 0)
    )?.toLocaleString('vi');
  };

  return (
    members?.length > 0 && (
      <div className="grid">
        <div className="col-12">
          <DataTable value={members} className="p-datatable-gridlines" showGridlines dataKey="id">
            <Column
              header={t('formLayout.numericalOrder')}
              body={formatIndex}
              style={{ width: '2rem', textAlign: 'center' }}
            />
            <Column
              header={`${t(
                'projectProposal.general.researchHumanResources.fields.academicRank'
              )}, ${t('projectProposal.general.researchHumanResources.fields.academicDegree')}, ${t(
                'projectProposal.general.researchHumanResources.fields.name'
              )}`}
              body={formatFullname}
              className="member-name"
            />
            <Column header="Email" field="contact.email" style={{ minWidth: '8rem' }} />
            <Column
              header={t('council.member.fields.role')}
              style={{ minWidth: '9rem' }}
              body={formatRole}
            />
            <Column
              body={formatOrganization}
              header={t('projectProposal.general.researchHumanResources.fields.organization')}
              style={{ minWidth: '8rem' }}
            />
            {isShowScore && (
              <Column
                header={t('council.view.score')}
                body={formatScore}
                style={{ minWidth: '2rem', textAlign: 'center' }}
              />
            )}
            {isShowProposedExpenditure && (
              <Column
                header={t('council.view.proposedExpenditure')}
                body={formatProposedExpenditure}
                style={{ minWidth: '2rem', textAlign: 'center' }}
              />
            )}
            {isShowResult && (
              <Column
                body={formatResult}
                header={t('council.fields.result')}
                style={{ width: '4rem', maxWidth: '6rem' }}
              />
            )}

            {isShowResultReviewPrequalification && (
              <Column
                header={t('resultReview.title')}
                body={formatResultReviewPrequalification}
                style={{ minWidth: '8rem', textAlign: 'center' }}
              />
            )}

            {isShowStatus && (
              <Column
                header={t('council.fields.status')}
                body={formatStatus}
                style={{ minWidth: '8rem', textAlign: 'center' }}
              />
            )}
          </DataTable>
        </div>
      </div>
    )
  );
}

Members.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array.isRequired,
  council: PropTypes.shape().isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  councilType: PropTypes.shape().isRequired,
  setIsFormModalOpen: PropTypes.func,
  setIsDetailModalOpen: PropTypes.func,
  setCouncilMemberId: PropTypes.func,
  projectProposalId: PropTypes.string.isRequired,
  councilId: PropTypes.string.isRequired,
  isShowScore: PropTypes.bool,
  isShowProposedExpenditure: PropTypes.bool,
  isShowResult: PropTypes.bool,
  isShowValidScore: PropTypes.bool,
  yearProposed: PropTypes.number,
  template: PropTypes.number,
  isShowStatus: PropTypes.bool,
  isShowResultReviewPrequalification: PropTypes.bool,
};

Members.defaultProps = {
  setIsFormModalOpen: () => null,
  setIsDetailModalOpen: () => null,
  setCouncilMemberId: () => null,
  isShowScore: false,
  isShowProposedExpenditure: false,
  isShowResult: false,
  isShowValidScore: false,
  yearProposed: null,
  template: null,
  isShowStatus: true,
  isShowResultReviewPrequalification: false,
};
